import './situazione-commesse.scss';

import React, { useEffect, useRef, useState } from "react";
import { DataGrid, DateBox, LoadPanel, Lookup, SelectBox } from 'devextreme-react';
import { 
    Column, Toolbar, DataGridRef, Sorting, Grouping, Scrolling, Pager, 
    Paging, Summary, GroupItem, Export, Item, DataGridTypes 
} from 'devextreme-react/data-grid';
import { useScreenSize, useScreenSizeClass } from '../../utils/media-query';
import useApiConfiguration from '../../apiConfig';
import { useSituazioneCommessaDataSource } from '../../datasources/situazioneCommessaDataSource';
import { PersonaleApi, CommesseApi } from '../../data';
import { saveAs } from "file-saver-es";
import { Workbook } from 'exceljs';
import { exportDataGrid } from 'devextreme/common/export/excel';

export default function SituazioneCommesse() {
    const gridRef = useRef<DataGridRef>(null);
    const apiConfiguration = useApiConfiguration();
    const { isLarge, isMedium } = useScreenSize();
    const screenSizeClass = useScreenSizeClass();

    const [loading, setLoading] = useState(false);
    const [personaleId, setPersonaleId] = useState<number>(null);
    const [commessaId, setCommessaId] = useState<number>(null);
    const [currentDate, setCurrentDate] = useState<Date>(new Date());
    const [dataDa, setDataDa] = useState<Date>(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
    const [dataA, setDataA] = useState<Date>(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0));
    const [dataSourcePersonale, setDataSourcePersonale] = useState<any[]>(null);
    const [dataSourceCommesse, setDataSourceCommesse] = useState<any[]>(null);
    const [canChangePersonale, setChangePersonale] = useState<boolean>(false);
    const [canChangeCommessa, setChangeCommessa] = useState<boolean>(false);

    const exportFormats = ['xlsx']
    const exportTexts = {
        exportAll: "Esporta tutto",
        exportSelectedRows: "Esporta righe selezionate",
        exportTo: "Esporta in formato"
    };

    function changeCommessa(e: any) {
        setCommessaId(e.value);
    };

    function changeDipendente(e: any) {
        setPersonaleId(e.value);
    };

    const onExporting = (e: DataGridTypes.ExportingEvent) => {
        if (e.format == 'xlsx') {
            setLoading(true);

            const workbook = new Workbook();
            const worksheet = workbook.addWorksheet('Situazione Commesse');

            exportDataGrid({
                component: e.component,
                worksheet,
                autoFilterEnabled: true
            }).then(() => {
                workbook.xlsx.writeBuffer().then((buffer) => {
                    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'SituazioneCommesse.xlsx');
                });
            });

            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        const commApi = new CommesseApi(apiConfiguration);
        const persApi = new PersonaleApi(apiConfiguration);

        setPersonaleId(null);
        setCommessaId(null);

        const p1 = commApi.getCommesseByRange({ dataInizio: dataDa, dataFine: dataA }).then((res) => {
            setDataSourceCommesse(res);
            setChangeCommessa(res.length > 1);
        });

        const p2 = persApi.getPersonale({ date: dataDa }).then((res) => {
            setDataSourcePersonale(res);
            setChangePersonale(res.length > 1);
        });

        Promise.allSettled([p1, p2]).then(() => {
            setLoading(false);
        });
    }, [dataDa, dataA]);

    return (
        <React.Fragment>
            {
                loading && <LoadPanel style={{ zIndex: 9999 }} shading={true} shadingColor='rgba(0, 0, 0, 0.4)' position={'center'} visible />
            }

            <div className='view-list-situazione-comm'>
                <div className={`view-wrapper view-wrapper-list list-page ${screenSizeClass}`}>
                    <DataGrid
                        id="gridSituazione" ref={gridRef} key="idLavoro"
                        dataSource={useSituazioneCommessaDataSource(personaleId, commessaId, dataDa, dataA)}
                        showBorders rowAlternationEnabled
                        onExporting={onExporting}
                        noDataText='Nessuna commessa da visualizzare'
                        className='grid theme-dependent'
                    >
                        <Sorting mode="single" />
                        <Scrolling mode='virtual' />
                        <Grouping autoExpandAll expandMode='rowClick' />

                        <Export 
                            enabled allowExportSelectedData={false}
                            texts={exportTexts}
                            formats={exportFormats}
                        />

                        {/* <Paging defaultPageSize={10} />
                        <Pager visible={true} allowedPageSizes={[5, 10, 20]} displayMode='full' showPageSizeSelector showInfo showNavigationButtons infoText='{0} di {1} - Elementi: {2}' /> */}

                        <Toolbar>
                            <Item location="before">
                                <div className="grid-header">Situazione Commesse</div>
                            </Item>

                            <Item
                                location='center'
                                locateInMenu={ isLarge || isMedium ? "never" : "auto" }
                                showText='inMenu'
                                widget='dxDateBox'
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexDirection: (isLarge || isMedium ? "row" : "column") }}>
                                    <DateBox className='dataSearch'
                                        label="Da Data" labelMode='floating'
                                        value={dataDa} type='date'
                                        displayFormat="dd/MM/yyyy"
                                        inputAttr={{ readonly: true }}
                                        openOnFieldClick 
                                        onValueChanged={(e) => setDataDa(e.value)}
                                    />

                                    <DateBox className='dataSearch' 
                                        label="A Data" labelMode='floating'
                                        value={dataA} type='date'
                                        displayFormat="dd/MM/yyyy"
                                        inputAttr={{ readonly: true }}
                                        openOnFieldClick 
                                        onValueChanged={(e) => setDataA(e.value)}
                                    />
                                </div>
                            </Item>

                            <Item
                                location='center'
                                locateInMenu='auto'
                                showText='inMenu'
                                visible={canChangeCommessa}
                            >
                                <Lookup className='dx-commessa'
                                    label='Commessa' labelMode='floating'
                                    dataSource={dataSourceCommesse}
                                    displayExpr="descrizione" valueExpr="idCommessa"
                                    searchEnabled showClearButton
                                    value={commessaId}
                                    onValueChanged={changeCommessa}
                                    dropDownOptions={{ width: "auto" }}
                                />
                            </Item>

                            <Item
                                location='center'
                                locateInMenu='auto'
                                showText='inMenu'
                                widget='dxSelectBox'
                                visible={canChangePersonale}
                            >
                                <SelectBox className='dx-dipendente' 
                                    label='Dipendente' labelMode='floating'
                                    dataSource={dataSourcePersonale}
                                    displayExpr="descrizione" valueExpr="idPersonale"
                                    searchEnabled showClearButton
                                    value={personaleId}
                                    onValueChanged={changeDipendente}
                                />
                            </Item>
                            <Item name='exportButton' locateInMenu='never' location='after' />
                        </Toolbar>

                        <Column dataField='idLavoro' caption='' visible={false} allowExporting={false} />
                        <Column dataField='commessa' groupIndex={0} />
                        <Column dataField='personale' />
                        <Column dataField='data' dataType='date' />
                        <Column dataField='note' hidingPriority={(isLarge || isMedium) ? -1 : 0} />
                        <Column dataField='oreLavorate' />

                        <Summary>
                            <GroupItem 
                                column='oreLavorate'
                                summaryType='sum'
                                showInGroupFooter={false}
                                alignByColumn={true}
                                displayFormat="Ore totali: {0}"
                                valueFormat={{ type: "decimal", precision: 1 }}
                            />
                        </Summary>
                    </DataGrid>
                </div>
            </div>
        </React.Fragment>
    )
}